/**
 * Image Classification Domain Models
 * Shared types for AI image classification and metadata handling
 */

export type ClassificationType = 'fuse_cutout' | 'consumer_unit' | 'isolator_switch';

/**
 * Request payload for image classification API
 */
export interface ImageClassificationRequest {
  image: string;
  classificationType?: ClassificationType;
}

/**
 * Parameters accepted by ImageClassificationService.classifyImage
 */
export interface ClassifyImageParams {
  base64Image: string;
  deviceContext?: 'mobile' | 'desktop';
  options?: ClassificationOptions;
  classificationType?: ClassificationType;
}

/**
 * Response from the image classification API
 */
export interface ImageClassificationResponse {
  status: 'success' | 'quality_too_low' | 'error';
  quality_score?: number;
  quality_details?: QualityDetails;
  image_metadata?: ImageMetadata;
  classification?: ClassificationData;
  warnings: string[];
  error?: string;
}

export interface ImageClassificationError {
  detail?: string;
  status?: number;
  title?: string;
  error?: string;
}

/**
 * Image metadata
 */
export interface ImageMetadata {
  width: number;
  height: number;
  channels: number;
  original_format: string;
  original_size: [number, number] | null;
}

/**
 * Detection object from the API
 */
export interface Detection {
  bbox: [number, number, number, number]; // [x1, y1, x2, y2]
  label: string;
  confidence: number; // Percentage (0-100)
}

/**
 * Classification results from the API
 */
export interface ClassificationData {
  primary_classification: 'FuseCutOut' | 'ConsumerUnit' | 'IsolatorSwitch' | string;
  primary_confidence: number; // Percentage (0-100)
  fuse_cutout_confidence: number; // Percentage (0-100)
  consumer_unit_confidence: number; // Percentage (0-100)
  isolator_switch_confidence: number; // Percentage (0-100)
  class_confidences: Record<string, number> | null;
  confidence_threshold_met: boolean;
  raw_results: {
    count: number;
    detections: Detection[];
    success: boolean;
  };
  bounding_boxes: Detection[] | null;
  detection_settings:
    | Record<string, any>
    | {
        conf?: number;
        iou?: number;
        img?: number;
        expand?: number;
      };
}

/**
 * Options for configuring classification behavior
 */
export interface ClassificationOptions {
  enabled?: boolean;
  autoPopulateForm?: boolean;
  confidenceThreshold?: number;
  minResolution?: number;
  outputQuality?: number;
  includeProcessedImage?: boolean;
  minBlurScore?: number;
  minExposureScore?: number;
  timeout?: number;
  showResults?: boolean;
  additionalOptions?: { [key: string]: any };
}

/**
 * Quality assessment details
 */
export interface QualityDetails {
  // Core scores
  resolution_score: number;
  blur_score: number;
  exposure_score: number;
  is_acceptable: boolean;
  issues: string[];

  // Threshold values
  resolution_score_threshold?: number;
  blur_score_threshold?: number;
  exposure_score_threshold?: number;

  // Threshold met flags
  resolution_score_threshold_met?: boolean;
  blur_score_threshold_met?: boolean;
  exposure_score_threshold_met?: boolean;

  // Megapixels
  megapixels?: number;
  megapixels_threshold?: number;
  megapixels_threshold_met?: boolean;

  // Overall quality
  overall_quality?: number;
  overall_quality_threshold?: number;
  overall_quality_threshold_met?: boolean;
}

/**
 * Simplified classification result for UI consumption and S3 metadata
 */
export interface ClassificationResult {
  status?: string;
  success: boolean;
  equipmentType: string;
  confidence: number;
  fuseCutoutConfidence: number;
  consumerUnitConfidence: number;
  isolatorSwitchConfidence: number;
  classConfidences?: Record<string, number> | null;
  confidenceThresholdMet: boolean;
  qualityScore: number;
  qualityAcceptable: boolean;
  qualityIssues: string[];
  qualityDetails?: QualityDetails;
  warnings: string[];
  error?: string;
  errorDetails?: string[];
  boundingBoxes?: Array<{
    label: string;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    confidence: number;
  }>;
  detectionSettings?: {
    conf: number;
    iou: number;
    img: number;
    expand: number;
  };
  imageWidth?: number;
  imageHeight?: number;
  sourceWidth?: number;
  sourceHeight?: number;
}

/**
 * Image with associated classification metadata
 * Used when passing classified images through the form submission flow
 */
export interface ImageWithMetadata {
  imageData: string; // base64 encoded image string
  metadata?: ClassificationResult;
}

/**
 * Type guard to check if a value is an ImageWithMetadata object
 */
export function isImageWithMetadata(value: any): value is ImageWithMetadata {
  return value !== null && typeof value === 'object' && 'imageData' in value && typeof value.imageData === 'string';
}

/**
 * Normalize metadata to match S3 header string format
 * Ensures backend receives exactly what will be sent to S3 headers
 * This prevents signature mismatches between what backend signs and what frontend uploads
 *
 * ALL VALUES ARE CONVERTED TO STRINGS to match backend serialization.
 * This eliminates any boolean→string or number→string conversion mismatches.
 *
 * @param result - Raw classification result from AI service
 * @returns Normalized metadata with all string values (typed as any for flexibility)
 */
export function normalizeMetadataForStorage(result: ClassificationResult): any {
  return {
    success: result.success,
    equipmentType: result.equipmentType || 'Unknown',
    confidence: (result.confidence ?? 0).toString(),
    fuseCutoutConfidence: (result.fuseCutoutConfidence ?? 0).toString(),
    consumerUnitConfidence: (result.consumerUnitConfidence ?? 0).toString(),
    isolatorSwitchConfidence: (result.isolatorSwitchConfidence ?? 0).toString(),
    qualityScore: (result.qualityScore ?? 0).toString(),
    confidenceThresholdMet: Boolean(result.confidenceThresholdMet).toString(),
    qualityAcceptable: Boolean(result.qualityAcceptable).toString(),
    qualityIssues: result.qualityIssues || [],
    qualityDetails: result.qualityDetails,
    warnings: result.warnings || [],
    boundingBoxes: result.boundingBoxes || [],
    detectionSettings: result.detectionSettings,
    error: result.error,
    errorDetails: result.errorDetails,
    // Image dimensions
    imageWidth: result.imageWidth,
    imageHeight: result.imageHeight,
    sourceWidth: result.sourceWidth ?? result.imageWidth,
    sourceHeight: result.sourceHeight ?? result.imageHeight
  };
}

/**
 * Deserialize JumpTech custom headers (x-jt-meta-*) from HTTP response
 * Used when fetching images through the API that exposes S3 metadata as custom headers
 *
 * @param headers - Angular HttpHeaders object from HTTP response
 * @returns ClassificationResult or null if no metadata present
 */
export function deserializeJtMetadataFromHeaders(headers: any): ClassificationResult | null {
  // Check if we have the HttpHeaders.get method (Angular HttpHeaders)
  if (!headers || typeof headers.get !== 'function') {
    return null;
  }

  // Check if metadata exists (equipment-type is required field)
  const equipmentType = headers.get('x-jt-meta-equipment-type');
  if (!equipmentType) {
    return null;
  }

  const result: ClassificationResult = {
    success: true,
    equipmentType: equipmentType || 'Unknown',
    confidence: parseFloat(headers.get('x-jt-meta-confidence') || '0'),
    fuseCutoutConfidence: 0, // Not stored in headers
    consumerUnitConfidence: 0, // Not stored in headers
    isolatorSwitchConfidence: 0, // Not stored in headers
    confidenceThresholdMet: headers.get('x-jt-meta-confidence-threshold-met') === 'true',
    qualityScore: parseFloat(headers.get('x-jt-meta-quality-score') || '0'),
    qualityAcceptable: headers.get('x-jt-meta-quality-acceptable') === 'true',
    sourceHeight: parseFloat(headers.get('x-jt-meta-source-height') || '0'),
    sourceWidth: parseFloat(headers.get('x-jt-meta-source-width') || '0'),
    qualityIssues: [],
    warnings: []
  };

  // Reconstruct quality details if available
  const blurScore = headers.get('x-jt-meta-blur-score');
  const exposureScore = headers.get('x-jt-meta-exposure-score');
  const resolutionScore = headers.get('x-jt-meta-resolution-score');

  if (blurScore || exposureScore || resolutionScore) {
    result.qualityDetails = {
      blur_score: parseFloat(blurScore || '0'),
      exposure_score: parseFloat(exposureScore || '0'),
      resolution_score: parseFloat(resolutionScore || '0'),
      is_acceptable: result.qualityAcceptable,
      issues: []
    };
  }

  // Parse bounding boxes if present
  const boundingBoxesHeader = headers.get('x-jt-meta-bounding-boxes');
  if (boundingBoxesHeader) {
    try {
      result.boundingBoxes = JSON.parse(boundingBoxesHeader);
    } catch (e) {
      console.warn('Failed to parse bounding boxes from JT metadata headers', e);
      result.boundingBoxes = [];
    }
  } else {
    result.boundingBoxes = [];
  }

  // Parse quality issues if present
  const qualityIssuesHeader = headers.get('x-jt-meta-quality-issues');
  if (qualityIssuesHeader) {
    try {
      result.qualityIssues = JSON.parse(qualityIssuesHeader);
    } catch (e) {
      console.warn('Failed to parse quality issues from JT metadata headers', e);
      result.qualityIssues = [];
    }
  }

  return result;
}
